import React, { useEffect, useRef, useState } from 'react';
import { Group as GroupIcon } from '@material-ui/icons';
import {
  FormElementPlugin,
  FormElementProps,
  useDataSourceProps,
  useFormContext
} from '@sierralabs/forms-ui';
import { Group, GroupPicker } from '@sierralabs/user-ui';

import { FormConfigPicker, FormPickerConfig } from './config/FormConfigPicker';

const FormGroupPicker = <T extends object>({
  element,
  edit,
  disabled
}: FormElementProps<T, FormPickerConfig>) => {
  const { placeholder, multiple } = element.config || {};
  const { formValues, setFormValue } = useFormContext<T, FormPickerConfig>();
  const key = (element.dataSource as keyof T) || element.key;
  const [value, setValue] = useState((formValues[key] as any) || undefined);
  const previousValueRef = useRef(value);
  const dataSourceProps = useDataSourceProps(element, value);

  // refresh value if formValues change outside of the component
  useEffect(() => {
    if (previousValueRef.current !== formValues[key]) {
      setValue(formValues[key]);
      previousValueRef.current = formValues[key];
    }
  }, [formValues, key]);

  return (
    <GroupPicker
      multiple={multiple}
      disabled={edit || disabled}
      value={value}
      textFieldProps={{
        label: undefined,
        size: undefined,
        error: dataSourceProps.error,
        helperText: dataSourceProps.helperText,
        placeholder: placeholder || 'Select Group(s)'
      }}
      onChange={(groups: Group | Group[] | null) => {
        const ids = Array.isArray(groups)
          ? groups.map(group => group.id)
          : groups?.id || null;
        setFormValue(key, ids as any);
      }}
    />
  );
};

export const FormConfigGroupPicker: React.FC<FormPickerConfig> = () => {
  return <FormConfigPicker />;
};

export const FormGroupPickerPlugin: FormElementPlugin<any, FormPickerConfig> = {
  icon: <GroupIcon />,
  name: 'Team Picker',
  type: 'GroupPicker',
  dataTypes: ['integer'],
  configurator: FormConfigGroupPicker,
  element: FormGroupPicker
};
